import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../services/api';
import {
    UserCircleIcon,
    ChevronRightIcon,
    XMarkIcon,
    UserIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    PhoneIcon,
    EnvelopeIcon,
    LanguageIcon,
    IdentificationIcon
} from '@heroicons/react/24/outline';

interface ProfileCompletionModalProps {
    isOpen: boolean;
    onClose: () => void;
    missingFields: string[];
    onSuccess?: (updatedUser: any) => void;
    message?: string;
}

export default function ProfileCompletionModal({
    isOpen,
    onClose,
    missingFields,
    onSuccess,
    message = "To ensure transparency and comply with regulatory requirements, we need a few more details for your donor profile before you can proceed with a donation."
}: ProfileCompletionModalProps) {
    const { user, setUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [donorType, setDonorType] = useState<string>(() => {
        const raw = user?.donorType || user?.donor_type || 'RESIDENT_INDIA';
        if (raw === 'CITIZEN') return 'RESIDENT_INDIA';
        if (raw === 'NRI_INDIAN') return 'NRI';
        return raw;
    });
    const [inlineMessage, setInlineMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const [form, setForm] = useState({
        name: user?.name || user?.fullName || '',
        email: user?.email || '',
        phone: user?.phone || user?.phoneNumber || '',
        postalCode: user?.postalCode || (user as any)?.postal_code || '',
        state: user?.state || '',
        district: user?.district || '',
        panCardNo: user?.panCardNo || (user as any)?.pan_card_no || user?.orgPan || (user as any)?.org_pan || '',
    });

    useEffect(() => {
        if (user) {
            setForm(prev => ({
                ...prev,
                name: user.name || user.fullName || prev.name,
                email: user.email || prev.email,
                phone: user.phone || user.phoneNumber || prev.phone,
                postalCode: user.postalCode || (user as any).postal_code || prev.postalCode,
                state: user.state || prev.state,
                district: user.district || prev.district,
                panCardNo: user.panCardNo || (user as any).pan_card_no || user.orgPan || (user as any).org_pan || prev.panCardNo,
            }));
            const raw = user.donorType || user.donor_type || 'RESIDENT_INDIA';
            const normalized = raw === 'CITIZEN' ? 'RESIDENT_INDIA' : raw === 'NRI_INDIAN' ? 'NRI' : raw;
            setDonorType(normalized);
        }
    }, [user]);
    const pincodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    if (!isOpen) return null;

    const handlePincodeLookup = (pincode: string) => {
        if (pincodeTimerRef.current) clearTimeout(pincodeTimerRef.current);
        if (pincode.length !== 6 || !/^\d{6}$/.test(pincode)) return;
        pincodeTimerRef.current = setTimeout(async () => {
            setPincodeLoading(true);
            try {
                const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
                if (!response.ok) throw new Error('Failed to fetch postal code details');
                const data = await response.json();

                if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
                    const postOffice = data[0].PostOffice[0];
                    setForm(prev => ({
                        ...prev,
                        state: postOffice.State || prev.state,
                        district: postOffice.District || prev.district,
                    }));
                    setInlineMessage({ type: 'success', text: 'City & State auto-filled successfully!' });
                    setTimeout(() => setInlineMessage(null), 3500);
                }
            } catch (error) {
                setInlineMessage({ type: 'error', text: 'Could not fetch details for this pincode.' });
            } finally {
                setPincodeLoading(false);
            }
        }, 400);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setInlineMessage(null);

        const digits = form.phone.replace(/\D/g, '');
        if (!digits) {
            setInlineMessage({ type: 'error', text: 'Mobile number is required' });
            return;
        }
        if (digits.length !== 10) {
            setInlineMessage({ type: 'error', text: 'Enter a valid 10-digit mobile number' });
            return;
        }
        if (!/^[6-9]/.test(digits)) {
            setInlineMessage({ type: 'error', text: 'Mobile number must start with 6, 7, 8 or 9' });
            return;
        }
        if (!form.panCardNo?.trim()) {
            setInlineMessage({ type: 'error', text: 'PAN number is required' });
            return;
        }
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(form.panCardNo.trim())) {
            setInlineMessage({ type: 'error', text: 'Enter a valid PAN — e.g. ABCDE1234F' });
            return;
        }

        setLoading(true);

        try {
            const payload: any = {
                name: form.name,
                postalCode: form.postalCode,
                state: form.state,
                district: form.district,
                city: form.district,
                donorType: donorType,
            };

            if (donorType === 'ORGANIZATION') {
                payload.orgName = form.name;
                payload.orgRepPhone = form.phone;
                payload.orgPan = form.panCardNo;
            } else {
                payload.phoneNumber = form.phone;
                payload.panCardNo = form.panCardNo;
            }

            const response = await userAPI.updateProfile(payload);
            if (response.data?.user) {
                setUser(response.data.user);
            }
            setInlineMessage({ type: 'success', text: 'Profile completed successfully!' });
            setTimeout(() => {
                onSuccess?.(response.data.user || user);
                onClose();
            }, 1000);
        } catch (error: any) {
            setInlineMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update profile. Please verify your details.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
            <div
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity animate-fadeIn"
                onClick={onClose}
            />

            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                <form
                    onSubmit={handleSubmit}
                    className="relative transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg animate-scaleIn"
                >
                    <div className="bg-gradient-to-r from-primary-600 to-accent-500 h-20 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12 blur-xl"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-white/20 p-2.5 rounded-2xl backdrop-blur-md border border-white/30">
                                <UserCircleIcon className="w-8 h-8 text-white" />
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="absolute right-4 top-4 p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="px-5 py-5">
                        <div className="text-center mb-4">
                            <h3 className="text-xl font-black text-gray-900 tracking-tight mb-1">
                                Complete Your Profile
                            </h3>
                            <p className="text-gray-500 text-xs leading-tight max-w-sm mx-auto">
                                We need a few more details to comply with donor regulations.
                            </p>
                        </div>

                        <div className="bg-gray-50 p-1 rounded-xl flex gap-1 mb-5 border border-gray-200">
                            {[
                                { value: 'RESIDENT_INDIA', label: 'Resident', icon: UserIcon },
                                { value: 'NRI', label: 'NRI', icon: LanguageIcon },
                                { value: 'ORGANIZATION', label: 'Organization', icon: BuildingOfficeIcon },
                            ].map(({ value, label, icon: Icon }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setDonorType(value)}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-[11px] transition-all ${donorType === value ? 'bg-white text-primary-600 shadow-sm border border-gray-100' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <ModalInput
                                        icon={donorType === 'ORGANIZATION' ? <BuildingOfficeIcon className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
                                        label={donorType === 'ORGANIZATION' ? "Organization Name" : "Full Name"}
                                        value={form.name}
                                        onChange={(v: string) => setForm({ ...form, name: v })}
                                        required
                                        placeholder={donorType === 'ORGANIZATION' ? "e.g. Imarat Shariah" : "Your full name"}
                                    />
                                </div>
                                <ModalInput
                                    icon={<EnvelopeIcon className="w-4 h-4" />}
                                    label={donorType === 'ORGANIZATION' ? "Org Email" : "Email Address"}
                                    type="email"
                                    value={form.email}
                                    onChange={(v: string) => setForm({ ...form, email: v })}
                                    required
                                    disabled={!!user?.email}
                                />
                                <ModalInput
                                    icon={<PhoneIcon className="w-4 h-4" />}
                                    label={donorType === 'ORGANIZATION' ? "Contact Phone" : "Phone Number"}
                                    type="tel"
                                    value={form.phone}
                                    onChange={(v: string) => setForm({ ...form, phone: v })}
                                    required
                                />
                                <div className="relative">
                                    <ModalInput
                                        icon={<MapPinIcon className="w-4 h-4" />}
                                        label="Pincode"
                                        value={form.postalCode}
                                        onChange={(v: string) => {
                                            const cleanV = v.replace(/\D/g, '').slice(0, 6);
                                            setForm({ ...form, postalCode: cleanV });
                                            if (cleanV.length === 6) handlePincodeLookup(cleanV);
                                        }}
                                        required
                                        maxLength={6}
                                        placeholder="6-digits"
                                    />
                                    {pincodeLoading && (
                                        <div className="absolute right-3 bottom-2.5">
                                            <div className="w-3.5 h-3.5 border-2 border-primary-500/20 border-t-primary-500 rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                </div>
                                <ModalInput
                                    icon={<LanguageIcon className="w-4 h-4" />}
                                    label="State"
                                    value={form.state}
                                    onChange={(v: string) => setForm({ ...form, state: v })}
                                    required
                                />
                                <ModalInput
                                    icon={<MapPinIcon className="w-4 h-4" />}
                                    label="District"
                                    value={form.district}
                                    onChange={(v: string) => setForm({ ...form, district: v })}
                                    required
                                />
                                <div className="col-span-2">
                                    <ModalInput
                                        icon={<IdentificationIcon className="w-4 h-4" />}
                                        label={(donorType === 'RESIDENT_INDIA' || donorType === 'NRI') ? "PAN Card Number" : "Organization PAN"}
                                        value={form.panCardNo}
                                        onChange={(v: string) => setForm({ ...form, panCardNo: v.toUpperCase() })}
                                        required
                                        maxLength={10}
                                        placeholder="ABCDE1234F"
                                    />
                                    <p className="text-[10px] text-amber-600 font-bold mt-1 ml-1 uppercase tracking-tight">
                                        Mandatory for 80G Tax Receipt
                                    </p>
                                </div>
                            </div>
                        </div>

                        {inlineMessage && (
                            <div className={`mt-4 px-4 py-3 rounded-lg text-xs font-bold leading-relaxed flex items-center gap-2 ${inlineMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60' : 'bg-rose-50 text-rose-700 border border-rose-200/60'}`}>
                                <span className="text-base">{inlineMessage.type === 'success' ? '✓' : '⚠'}</span>
                                {inlineMessage.text}
                            </div>
                        )}

                        <div className="mt-5 flex flex-col gap-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/20 disabled:opacity-50 group hover:-translate-y-0.5"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        Complete & Continue
                                        <ChevronRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full py-1 text-gray-500 font-bold hover:text-gray-600 transition-colors text-[10px] uppercase tracking-widest"
                            >
                                I'll do it later
                            </button>
                        </div>

                        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500">Secure & Confidential KYC</span>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ModalInput({ label, icon, value, onChange, type = "text", required = false, disabled = false, maxLength, placeholder }: any) {
    const inputId = `modal-${label.replace(/\s+/g, '-').toLowerCase()}`;
    return (
        <div className="space-y-1">
            <label htmlFor={inputId} className="text-xs font-extrabold uppercase tracking-wider text-gray-500 ml-1 flex items-center gap-1">
                {icon} {label} {required && <span className="text-primary-500">*</span>}
            </label>
            <input
                id={inputId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                disabled={disabled}
                maxLength={maxLength}
                placeholder={placeholder}
                className="w-full px-4 py-2.5 bg-slate-50 border border-gray-300 rounded-xl focus:border-primary-500 focus:bg-white focus:ring-4 focus:ring-primary-500/5 transition-all outline-none font-bold text-gray-900 text-sm placeholder:text-gray-300 disabled:opacity-60"
            />
        </div>
    );
}
